"use client";

import { Zap, RotateCcw, Lock, type LucideIcon } from "@/components/icons";

export const languages = [
  { id: "python", label: "Python", iconSrc: "/python-icon.svg", color: "#3776ab" },
  { id: "nodejs", label: "Node.js", iconSrc: "/nodejs-icon.svg", color: "#339933" },
  { id: "go", label: "Go", iconSrc: "/go-icon.svg", color: "#00add8" },
  { id: "php", label: "PHP", iconSrc: "/php-icon.svg", color: "#777bb4" },
  { id: "curl", label: "cURL", iconSrc: "/curl-icon.svg", color: "#073551" },
  { id: "java", label: "Java", iconSrc: "/java-icon.svg", color: "#ed8b00" },
  { id: "csharp", label: "C#", iconSrc: "/csharp-icon.svg", color: "#239120" },
];

export const codeSnippets: Record<string, string> = {
  python: `import requests

# Proxidize configuration
USERNAME = "your_username"
PASSWORD = "your_password"
HOST     = "gate.proxidize.com"
PORT     = 7777

proxy_url = f"http://{USERNAME}:{PASSWORD}@{HOST}:{PORT}"

proxies = {
    "http":  proxy_url,
    "https": proxy_url,
}

# Make a request through the proxy
response = requests.get(
    "https://api.ipify.org?format=json",
    proxies=proxies,
    timeout=30,
)

print(response.json())  # {"ip": "185.241.x.x"}`,

  nodejs: `import axios from "axios";
import { HttpsProxyAgent } from "https-proxy-agent";

const USERNAME = "your_username";
const PASSWORD = "your_password";
const HOST     = "gate.proxidize.com";
const PORT     = 7777;

const proxyUrl = \`http://\${USERNAME}:\${PASSWORD}@\${HOST}:\${PORT}\`;
const agent    = new HttpsProxyAgent(proxyUrl);

const response = await axios.get(
  "https://api.ipify.org?format=json",
  { httpsAgent: agent }
);

console.log(response.data); // { ip: "185.241.x.x" }`,

  go: `package main

import (
    "fmt"
    "net/http"
    "net/url"
    "io"
)

func main() {
    proxyURL, _ := url.Parse(
        "http://your_username:your_password@gate.proxidize.com:7777",
    )

    client := &http.Client{
        Transport: &http.Transport{
            Proxy: http.ProxyURL(proxyURL),
        },
    }

    resp, err := client.Get("https://api.ipify.org")
    if err != nil {
        panic(err)
    }
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)
    fmt.Println(string(body)) // 185.241.x.x
}`,

  php: `<?php

$username = "your_username";
$password = "your_password";
$host     = "gate.proxidize.com";
$port     = 7777;

$ch = curl_init("https://api.ipify.org?format=json");

curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_PROXY          => "$host:$port",
    CURLOPT_PROXYUSERPWD   => "$username:$password",
    CURLOPT_SSL_VERIFYPEER => false,
    CURLOPT_TIMEOUT        => 30,
]);

$response = curl_exec($ch);
curl_close($ch);

echo $response; // {"ip":"185.241.x.x"}`,

  curl: `# Set your proxy once
PROXY="http://your_username:your_password@gate.proxidize.com:7777"

# Basic usage
curl --proxy "$PROXY" https://api.ipify.org?format=json

# With SOCKS5 support
SOCKS="your_username:your_password@gate.proxidize.com:7778"
curl --socks5 "$SOCKS" https://api.ipify.org?format=json

# Country targeting (Germany)
PROXY_DE="http://your_username-country-de:your_password@gate.proxidize.com:7777"
curl --proxy "$PROXY_DE" https://api.ipify.org?format=json

# Response: {"ip":"185.241.x.x"}`,

  java: `import java.net.*;
import java.net.http.*;
import java.time.Duration;

public class ProxidizeExample {
    public static void main(String[] args) throws Exception {
        String username = "your_username";
        String password = "your_password";
        String host     = "gate.proxidize.com";
        int    port     = 7777;

        Authenticator.setDefault(new Authenticator() {
            @Override
            protected PasswordAuthentication getPasswordAuthentication() {
                return new PasswordAuthentication(username, password.toCharArray());
            }
        });

        HttpClient client = HttpClient.newBuilder()
            .proxy(ProxySelector.of(new InetSocketAddress(host, port)))
            .connectTimeout(Duration.ofSeconds(10))
            .build();

        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create("https://api.ipify.org?format=json"))
            .build();

        HttpResponse<String> response =
            client.send(request, HttpResponse.BodyHandlers.ofString());

        System.out.println(response.body()); // {"ip":"185.241.x.x"}
    }
}`,

  csharp: `using System;
using System.Net;
using System.Net.Http;
using System.Threading.Tasks;

class ProxidizeExample
{
    static async Task Main()
    {
        const string username = "your_username";
        const string password = "your_password";
        const string host     = "gate.proxidize.com";
        const int    port     = 7777;

        var proxy = new WebProxy($"http://{host}:{port}")
        {
            Credentials = new NetworkCredential(username, password),
        };

        using var handler = new HttpClientHandler { Proxy = proxy };
        using var client  = new HttpClient(handler);

        var response = await client.GetStringAsync(
            "https://api.ipify.org?format=json"
        );

        Console.WriteLine(response); // {"ip":"185.241.x.x"}
    }
}`,
};

export type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export const features: Feature[] = [
  {
    icon: Zap,
    title: "5-minute setup",
    description: "One endpoint, one credential pair. Works with every HTTP client.",
  },
  {
    icon: RotateCcw,
    title: "Smart rotation",
    description: "Auto-rotate IPs on every request, or pin a session.",
  },
  {
    icon: Lock,
    title: "All protocols",
    description: "HTTP, HTTPS, and SOCKS5 proxies. Whatever your stack uses, we support.",
  },
];

export const codeVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 0 },
};
